import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import { Eye, EyeOff, ArrowLeft } from "lucide-react";
import { updateSignupField, resetSignupForm } from "./redux/signupSlice";
import { signupUser } from "./redux/signupThunk";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import "./Signup.css";
import userStore from "../../../zustand/userStore";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.2 },
  },
};

const fieldVariants = {
  hidden: { opacity: 0, x: -30 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { type: "spring", stiffness: 100, damping: 15 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, scale: 0.9, y: 40 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { type: "spring", stiffness: 80, damping: 20, duration: 0.6 },
  },
  exit: {
    opacity: 0,
    scale: 0.9,
    y: 40,
    transition: { duration: 0.3 },
  },
};

export default function Signup() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { formData, loader, error } = useSelector((state) => state.signup);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  // const setUser = userStore.getState().setUser;

  const validate = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = "Name is required";
    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Invalid email format";
    }
    if (!formData.contactNumber.trim()) {
      errors.contactNumber = "Contact number is required";
    } else if (!/^[0-9]{10}$/.test(formData.contactNumber.replace(/\s/g, ""))) {
      errors.contactNumber = "Enter a valid 10-digit number";
    }
    if (!formData.password) {
      errors.password = "Password is required";
    } else if (formData.password.length < 6) {
      errors.password = "Password must be at least 6 characters";
    }
    if (!formData.confirmPassword) {
      errors.confirmPassword = "Please confirm your password";
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }
    return errors;
  };

  const handleChange = (field, value) => {
    dispatch(updateSignupField({ field, value }));
    if (validationErrors[field]) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    setValidationErrors({});
    const { confirmPassword, ...submitData } = formData;

    dispatch(signupUser(submitData))
      .unwrap()
      .then((data) => {
        // setUser(data.data)
        alert(
          "OTP has been sent to your email. Please verify to complete registration." ||
            data.message,
        );
        // toast.success(data.message || "Registered successfully");
        // navigate("/");
        // navigate("/login");
        navigate("/otp", {
          state: { formData: submitData },
        });
      })
      .catch((err) => {
        toast.error(err || "Signup failed");
      });
  };

  const handleBackToHome = () => {
    dispatch(resetSignupForm());
    navigate("/");
  };

  return (
    <div className="signup-page">
      <Toaster position="top-right" />
      {/* Animated background blobs */}
      <motion.div
        className="signup-blob signup-blob-1"
        animate={{
          x: [0, 30, -20, 0],
          y: [0, -40, 20, 0],
          scale: [1, 1.1, 0.95, 1],
        }}
        transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="signup-blob signup-blob-2"
        animate={{
          x: [0, -40, 25, 0],
          y: [0, 30, -30, 0],
          scale: [1, 0.95, 1.1, 1],
        }}
        transition={{ duration: 25, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="signup-blob signup-blob-3"
        animate={{
          x: [0, 20, -30, 0],
          y: [0, -20, 40, 0],
          scale: [1, 1.05, 0.9, 1],
        }}
        transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
      />

      <AnimatePresence mode="wait">
        <motion.div
          key="form"
          className="signup-card"
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          {/* Back button */}
          <motion.button
            className="signup-back-btn"
            onClick={handleBackToHome}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <ArrowLeft size={20} />
          </motion.button>

          <div className="signup-card-header">
            <motion.h1
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              Create Account
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              Fill in the details to get started
            </motion.p>
          </div>

          <motion.form
            className="signup-form"
            onSubmit={handleSubmit}
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {/* Full Name */}
            <motion.div
              className={`signup-field ${validationErrors.name ? "signup-field-error" : ""}`}
              variants={fieldVariants}
            >
              <label htmlFor="name">Full Name</label>
              <div className="signup-input-wrapper">
                <input
                  id="name"
                  type="text"
                  placeholder="Enter your full name"
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                />
              </div>
              <AnimatePresence>
                {validationErrors.name && (
                  <motion.span
                    className="signup-error-text"
                    initial={{ opacity: 0, y: -5, height: 0 }}
                    animate={{ opacity: 1, y: 0, height: "auto" }}
                    exit={{ opacity: 0, y: -5, height: 0 }}
                  >
                    {validationErrors.name}
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.div>

            {/* Email Address */}
            <motion.div
              className={`signup-field ${validationErrors.email ? "signup-field-error" : ""}`}
              variants={fieldVariants}
            >
              <label htmlFor="email">Email Address</label>
              <div className="signup-input-wrapper">
                <input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  autoComplete="email"
                />
              </div>
              <AnimatePresence>
                {validationErrors.email && (
                  <motion.span
                    className="signup-error-text"
                    initial={{ opacity: 0, y: -5, height: 0 }}
                    animate={{ opacity: 1, y: 0, height: "auto" }}
                    exit={{ opacity: 0, y: -5, height: 0 }}
                  >
                    {validationErrors.email}
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.div>

            {/* Contact Number */}
            <motion.div
              className={`signup-field ${validationErrors.contactNumber ? "signup-field-error" : ""}`}
              variants={fieldVariants}
            >
              <label htmlFor="contactNumber">Contact Number</label>
              <div className="signup-input-wrapper">
                <input
                  id="contactNumber"
                  type="tel"
                  placeholder="Enter your contact number"
                  value={formData.contactNumber}
                  onChange={(e) =>
                    handleChange("contactNumber", e.target.value)
                  }
                />
              </div>
              <AnimatePresence>
                {validationErrors.contactNumber && (
                  <motion.span
                    className="signup-error-text"
                    initial={{ opacity: 0, y: -5, height: 0 }}
                    animate={{ opacity: 1, y: 0, height: "auto" }}
                    exit={{ opacity: 0, y: -5, height: 0 }}
                  >
                    {validationErrors.contactNumber}
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.div>

            {/* Password */}
            <motion.div
              className={`signup-field ${validationErrors.password ? "signup-field-error" : ""}`}
              variants={fieldVariants}
            >
              <label htmlFor="password">Password</label>
              <div className="signup-input-wrapper">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Create a password"
                  value={formData.password}
                  onChange={(e) => handleChange("password", e.target.value)}
                  autoComplete="off"
                />
                <button
                  type="button"
                  className="signup-eye-btn"
                  onClick={() => setShowPassword((p) => !p)}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <AnimatePresence>
                {validationErrors.password && (
                  <motion.span
                    className="signup-error-text"
                    initial={{ opacity: 0, y: -5, height: 0 }}
                    animate={{ opacity: 1, y: 0, height: "auto" }}
                    exit={{ opacity: 0, y: -5, height: 0 }}
                  >
                    {validationErrors.password}
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.div>

            {/* Confirm Password */}
            <motion.div
              className={`signup-field ${validationErrors.confirmPassword ? "signup-field-error" : ""}`}
              variants={fieldVariants}
            >
              <label htmlFor="confirmPassword">Confirm Password</label>
              <div className="signup-input-wrapper">
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm your password"
                  value={formData.confirmPassword}
                  onChange={(e) =>
                    handleChange("confirmPassword", e.target.value)
                  }
                  autoComplete="off"
                />
                <button
                  type="button"
                  className="signup-eye-btn"
                  onClick={() => setShowConfirmPassword((p) => !p)}
                  tabIndex={-1}
                >
                  {showConfirmPassword ? (
                    <EyeOff size={18} />
                  ) : (
                    <Eye size={18} />
                  )}
                </button>
              </div>
              <AnimatePresence>
                {validationErrors.confirmPassword && (
                  <motion.span
                    className="signup-error-text"
                    initial={{ opacity: 0, y: -5, height: 0 }}
                    animate={{ opacity: 1, y: 0, height: "auto" }}
                    exit={{ opacity: 0, y: -5, height: 0 }}
                  >
                    {validationErrors.confirmPassword}
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.div>

            {error && (
              <motion.div
                className="signup-api-error"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                {error}
              </motion.div>
            )}

            <motion.button
              type="submit"
              className="signup-submit-btn"
              disabled={loader}
              whileHover={{
                scale: 1.02,
                boxShadow: "0 8px 30px rgba(99, 102, 241, 0.4)",
              }}
              whileTap={{ scale: 0.98 }}
              variants={fieldVariants}
            >
              {loader ? (
                <span className="signup-spinner-wrap">
                  <motion.span
                    className="signup-spinner"
                    animate={{ rotate: 360 }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                  />
                  Creating Account...
                </span>
              ) : (
                "Sign Up"
              )}
            </motion.button>
          </motion.form>

          <motion.p
            className="signup-footer-text"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9 }}
          >
            Already have an account?{" "}
            <span
              className="signup-login-link"
              onClick={() => {
                dispatch(resetSignupForm());
                navigate("/login");
              }}
            >
              Login
            </span>
          </motion.p>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
